// Ported from qr-phone-frontend src/app/modules/calling/peer.service.ts (v3.2.0.0).
// Only adaptation: the ICE config comes from core/iceConfig instead of the Redux store.
import { globals } from './globals'
import { socket } from './socket'
import { createLog } from './logger'
import { getIceConfig } from './iceConfig'

let pendingCandidates: any = []

export const constraints = {
  video: {
    width: {
      min: '300',
      max: '640',
    },
    height: {
      min: '200',
      max: '480',
    },
    echoCancellation: { exact: true },
    noiseSuppression: { exact: true },
    facingMode: 'user',
  },
  audio: {
    echoCancellation: { exact: true },
    noiseSuppression: { exact: true },
  },
}

export const setPeerConnection = (
  remoteUserSocketId: string,
  remoteUsername: string,
  me: string,
  location: any
) => {
  // TRY TO CREATE PEER CONNECTION.
  try {
    createLog('webrtc_debug', 'called set peer connection for ', remoteUserSocketId)
    pendingCandidates = []
    let peerConnection: RTCPeerConnection

    // ICE SERVER CONFIG, FETCHED FROM THE BACKEND AT FLOW START.
    const iceServerConfig = getIceConfig()

    // CONNECTION START TO RTC PEER
    peerConnection = new RTCPeerConnection(iceServerConfig as any)
    peerConnection.onicecandidate = (event: any) => handleICECandidateEvent(event, remoteUserSocketId)

    // STORE THE PEER CONNECTION TO GLOBAL VARIABLE.
    globals.peerConnection = peerConnection
    return peerConnection
  } catch (e) {
    console.error('error in starting peer connection ', e)
    return null
  }
}

// FORCE THE PEER CONNECTION TO ONLY USE TURN-RELAY CANDIDATES. WHEN BOTH ENDPOINTS ARE
// BEHIND CARRIER-GRADE / SYMMETRIC NAT (e.g. PHONE-TO-PHONE) NO HOST/SRFLX PAIR CAN FORM
// AND ONLY A RELAY<->RELAY PAIR TRAVERSES. APPLIED AS AN ESCALATION (NOT BY DEFAULT) WHEN
// MEDIA DOES NOT CONNECT IN TIME, SO ORDINARY CALLS KEEP THE CHEAPER DIRECT PATH. THE NEW
// POLICY TAKES EFFECT ON THE NEXT ICE RESTART. RETURNS true IF IT WAS APPLIED.
export const applyRelayPolicy = (peerConnection: RTCPeerConnection): boolean => {
  try {
    if (!peerConnection || typeof peerConnection.setConfiguration !== 'function') {
      return false
    }
    const iceServerConfig = getIceConfig()
    peerConnection.setConfiguration({ ...(iceServerConfig as any), iceTransportPolicy: 'relay' })
    createLog('webrtc_debug', 'forced ICE transport policy to relay')
    return true
  } catch (e) {
    createLog('webrtc_debug', 'could not force relay transport policy', 'warn', e)
    return false
  }
}

// HAND A FRESHLY FETCHED ICE SERVER LIST TO THE LIVE PEER CONNECTION (ROADMAP #11): THE
// CREDENTIALS IT WAS BUILT WITH ARE ABOUT TO EXPIRE. UNLIKE applyRelayPolicy THIS DOES NOT
// FORCE relay - IT ONLY REPLACES iceServers SO THE NEXT (CALLER-DRIVEN) ICE RESTART USES
// FRESH CREDENTIALS INSTEAD OF THE EXPIRING ONES.
export const applyRefreshedIceServers = (peerConnection: RTCPeerConnection, iceServerConfig: any): boolean => {
  try {
    if (!peerConnection || typeof peerConnection.setConfiguration !== 'function') {
      return false
    }
    peerConnection.setConfiguration({ ...peerConnection.getConfiguration(), iceServers: iceServerConfig?.iceServers })
    createLog('webrtc_debug', 'applied refreshed ICE servers ahead of credential expiry')
    return true
  } catch (e) {
    createLog('webrtc_debug', 'could not apply refreshed ICE servers', 'warn', e)
    return false
  }
}

export const handleICECandidateEvent = (event: any, remoteUserSocketId: string) => {
  if (event.candidate) {
    const obj = {
      candidate: event.candidate,
      callTo: remoteUserSocketId,
    }
    socket.emit('offer-candidate', obj)
  }
}

export const isThereVideoInPeer = (peerConnection: RTCPeerConnection) => {
  return peerConnection?.getSenders()?.some((sender) => {
    const track = sender.track
    return track && track.kind === 'video'
  })
}

export const isThereAudioInPeer = (peerConnection: RTCPeerConnection) => {
  return peerConnection?.getSenders()?.some((sender) => {
    const track = sender.track
    return track && track.kind === 'audio'
  })
}

// HANDLE WEBRTC NEGOTIATION. THIS SENDS A (POSSIBLY ICE-RESTART) OFFER TO THE REMOTE PEER.
export const sendOfferToRemoteUser = async (
  remoteUserSocketId: string,
  remoteUsername: string,
  me: string,
  location: any,
  iceRestart: boolean = false
) => {
  createLog('webrtc_debug', 'handling negotiation ', remoteUserSocketId, 'next: send offer.')
  try {
    const peerConnection: RTCPeerConnection = getPeerConnection()
    const hasVideoTrack = isThereVideoInPeer(peerConnection)

    const offerParams = {
      offerToReceiveAudio: true,
      offerToReceiveVideo: true,
      voiceActivityDetection: true,
      iceRestart: iceRestart,
    }

    // CREATE OFFER. WITH iceRestart THE OFFER CARRIES NEW ICE CREDENTIALS SO BOTH PEERS
    // GATHER FRESH CANDIDATES AND THE MEDIA PATH IS REBUILT.
    const offer = await peerConnection.createOffer(offerParams)
    await peerConnection.setLocalDescription(offer)

    if (iceRestart) {
      createLog('webrtc_debug', 'sending ICE-restart offer to', remoteUserSocketId)
    }

    const payload = {
      userToCall: remoteUserSocketId,
      signalData: offer,
      // PREFER THE LIVE SOCKET ID: AFTER A RECONNECTION THE `me` CAPTURED BY THE CALLING
      // COMPONENT STILL POINTS TO THE OLD (DEAD) SOCKET AND THE ANSWER WOULD BE LOST.
      from: socket.id || me,
      name: remoteUsername,
      data: location.state,
      mediaStatus: {
        type: !hasVideoTrack ? 'mic' : 'both',
        myMediaStatus: !hasVideoTrack ? true : [true, true],
      },
      callType: hasVideoTrack ? 'video' : 'audio',
    }
    socket.emit('offer', payload)
    createLog('webrtc_debug', 'offer sent. ', 'next: handle answer')
  } catch (error) {
    console.error('error code 106:', error)
  }
}

// CLOSE AND FORGET THE CURRENT PEER CONNECTION.
export const closeUserPeerConnections = () => {
  if (globals.peerConnection && typeof globals.peerConnection !== null) {
    globals.peerConnection?.getSenders()?.forEach((sender: any) => {
      sender?.track?.stop()
      globals.peerConnection.removeTrack(sender)
    })
    globals.peerConnection?.close()
    globals.peerConnection = null
  }
  pendingCandidates = []
}

export const getPeerConnection = () => {
  if (globals.peerConnection !== null) {
    return globals.peerConnection
  }
  return null
}

// TO ADD AN INCOMING ICE CANDIDATE (QUEUED UNTIL THE REMOTE DESCRIPTION IS SET).
export const onIceCandidateOffer = (incoming: { candidate: RTCIceCandidateInit | undefined }) => {
  const peerConnection = getPeerConnection()

  if (peerConnection !== null && peerConnection.remoteDescription) {
    try {
      const candidate = new RTCIceCandidate(incoming.candidate)
      peerConnection.addIceCandidate(candidate).catch((e: any) => {
        console.error('Error adding ICE candidate: ', e)
      })
    } catch (error) {
      console.error('Error in onIceCandidateOffer:', error)
    }
  } else {
    // STORE THE CANDIDATE FOR LATER WHEN THE REMOTE DESCRIPTION IS SET
    if (typeof pendingCandidates === 'undefined') {
      pendingCandidates = []
    }
    pendingCandidates.push(incoming.candidate)
  }
}

export const processPendingCandidates = (peerConnection: RTCPeerConnection) => {
  if (pendingCandidates && typeof pendingCandidates !== 'undefined' && pendingCandidates.length > 0) {
    pendingCandidates.forEach((candidate: any) => {
      peerConnection.addIceCandidate(new RTCIceCandidate(candidate)).catch((e) => {
        console.error('Error adding pending ICE candidate: ', e)
      })
    })
    pendingCandidates = []
  }
}

export const removeStoppedTrackFromPeerConnection = (track: MediaStreamTrack) => {
  const peerConnection: RTCPeerConnection = getPeerConnection()

  if (track.readyState === 'ended' && peerConnection) {
    const sender = peerConnection?.getSenders()?.find((s) => s.track === track)
    if (sender) {
      peerConnection.removeTrack(sender)
      createLog('webrtc_debug', 'Track removed from PeerConnection:', track.kind)
    } else {
      console.warn('No sender found for track:', track)
    }
  }
}
